import * as render from './apps-render.js';
import * as api from './apps-api.js';
import * as folder from './apps-folders.js';
import state from './apps-state.js';

export function findFirstAvailablePosition(folderId) {
    const folderIcons = window.icons.filter(ic => ic.kfolder === folderId && ic.region >= 0);
    const layout = render.calculateLayout();
    const isFolder = folderId !== 0;
    
    if (isFolder) {
        // Folder layout: single band of regions, but visually wraps based on layout.leaves
        const regionsPerScreen = layout.leaves;
        
        // Calculate how many screens we might need to search
        const maxRegion = Math.max(...folderIcons.map(ic => ic.region), -1);
        const screensToSearch = Math.ceil((maxRegion + 1) / regionsPerScreen) + 2;
        
        // Search screen by screen, then rank by rank within each screen
        for (let screenNum = 0; screenNum < screensToSearch; screenNum++) {
            // Search each of the 3 ranks
            for (let rank = 0; rank < 3; rank++) {
                const startPos = rank * 4; // position within region
                const endPos = startPos + 3;
                
                // Search across the regions visible on this screen
                for (let regionInScreen = 0; regionInScreen < regionsPerScreen; regionInScreen++) {
                    const region = screenNum * regionsPerScreen + regionInScreen;
                    
                    for (let position = startPos; position <= endPos; position++) {
                        const occupied = folderIcons.find(ic => ic.region === region && ic.position === position);
                        if (!occupied) {
                            return { region, position };
                        }
                    }
                }
            }
        }
        
        // Fallback
        return { region: maxRegion + 1, position: 0 };
    } else {
        // Main screen layout: leaf-major (region column-major) regions, each with 3 ranks of 4 icons
        const regionsPerScreen = layout.leaves * layout.bands;
        const maxRegion = Math.max(...folderIcons.map(ic => ic.region), -1);
        const screensToSearch = Math.ceil((maxRegion + 1) / regionsPerScreen) + 2;
        
        // For each screen
        for (let screenNum = 0; screenNum < screensToSearch; screenNum++) {
            const screenStartRegion = screenNum * regionsPerScreen;
            
            // Search visually: left to right, top to bottom
            const totalRanks = layout.bands * 3;
            const totalFiles = layout.leaves * 4;
            
            for (let rank = 0; rank < totalRanks; rank++) {
                for (let file = 0; file < totalFiles; file++) {
                    const slotInfo = render.visualPositionToRegionPosition(rank, file, layout);
                    if (!slotInfo) continue;
                    
                    const actualRegion = screenStartRegion + slotInfo.region;
                    
                    const occupied = folderIcons.find(ic => ic.region === actualRegion && ic.position === slotInfo.position);
                    if (!occupied) {
                        return { region: actualRegion, position: slotInfo.position };
                    }
                }
            }
        }
        
        // Fallback
        return { region: maxRegion + 1, position: 0 };
    }
}

// Edit modal
export function openEditModal(icon) {
    state.editingIcon = icon;
    document.getElementById('editTitle').value = icon.title;
    document.getElementById('editBadgeVisible').checked = icon.showBadge;
    document.getElementById('editModal').classList.add('open');
    
    // Hide virtual cursor and arrow while modal is open
    const cursor = document.getElementById('virtualCursor');
    const arrow = document.getElementById('virtualCursorArrow');
    if (cursor) {
        cursor.style.display = 'none';
        cursor.setAttribute('data-hidden-for-modal', 'true');
    }
    if (arrow) {
        arrow.style.display = 'none';
        arrow.setAttribute('data-hidden-for-modal', 'true');
    }
    
    // Disable tab navigation for nav items and auth buttons
    const navItems = document.querySelectorAll('.nav-item');
    navItems.forEach(item => {
        item.setAttribute('data-original-tabindex', item.getAttribute('tabindex') || '0');
        item.setAttribute('tabindex', '-1');
    });
    
    const authButtons = document.querySelectorAll('.auth-btn');
    authButtons.forEach(btn => {
        btn.setAttribute('data-original-tabindex', btn.getAttribute('tabindex') || '0');
        btn.setAttribute('tabindex', '-1');
    });
    
    // Add keyboard handler for modal
    const modalKeyHandler = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            window.saveIconEdit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            window.closeEditModal();
        }
    };
    
    const modal = document.getElementById('editModal');
    modal.addEventListener('keydown', modalKeyHandler);
    // Store handler reference for removal later
    modal._modalKeyHandler = modalKeyHandler;
    
    // Focus the first input field
    setTimeout(() => {
        document.getElementById('editTitle').focus();
        document.getElementById('editTitle').select();
    }, 0);
}

window.closeEditModal = function() {
    const modal = document.getElementById('editModal');
    modal.classList.remove('open');
    state.editingIcon = null;
    
    // Restore virtual cursor and arrow visibility
    const cursor = document.getElementById('virtualCursor');
    const arrow = document.getElementById('virtualCursorArrow');
    if (cursor && cursor.getAttribute('data-hidden-for-modal') === 'true') {
        cursor.style.display = '';
        cursor.removeAttribute('data-hidden-for-modal');
    }
    if (arrow && arrow.getAttribute('data-hidden-for-modal') === 'true') {
        arrow.style.display = '';
        arrow.removeAttribute('data-hidden-for-modal');
    }
    
    // Remove keyboard handler
    if (modal._modalKeyHandler) {
        modal.removeEventListener('keydown', modal._modalKeyHandler);
        modal._modalKeyHandler = null;
    }
    
    // Restore tab navigation for nav items
    const navItems = document.querySelectorAll('.nav-item');
    navItems.forEach(item => {
        const originalTabindex = item.getAttribute('data-original-tabindex');
        if (originalTabindex !== null) {
            item.setAttribute('tabindex', originalTabindex);
            item.removeAttribute('data-original-tabindex');
        }
    });
    
    // Restore tab navigation for auth buttons
    const authButtons = document.querySelectorAll('.auth-btn');
    authButtons.forEach(btn => {
        const originalTabindex = btn.getAttribute('data-original-tabindex');
        if (originalTabindex !== null) {
            btn.setAttribute('tabindex', originalTabindex);
            btn.removeAttribute('data-original-tabindex');
        }
    });
}

window.saveIconEdit = function () {
    if (!state.editingIcon) return;
    
    state.editingIcon.title = document.getElementById('editTitle').value;
    const showBadge = document.getElementById('editBadgeVisible').checked;
    
    state.editingIcon.showBadge = showBadge;
    
    api.updateIcon(state.editingIcon);
    window.closeEditModal();
    
    if (state.openFolder) {
        folder.openFolderView(state.openFolder);
    } else {
        render.renderLaunchpad();
    }
}

window.deleteIcon = async function() {
    if (!state.editingIcon) return;
    
    if (!confirm(`Remove "${state.editingIcon.title}" from your apps?`)) {
        return;
    }
    
    const iconId = state.editingIcon.id;
    const iconFolder = state.editingIcon.kfolder;
    window.icons = window.icons.filter(icon => icon.id !== iconId);

    api.deleteIcon(iconId);

    window.closeEditModal();
    
    // Check if we deleted from a folder that now has only 1 icon
    if (iconFolder !== 0) {
        const folderIcons = window.icons.filter(icon => icon.kfolder === iconFolder);
        if (folderIcons.length === 1) {
            // Decompose the folder
            const lastIcon = folderIcons[0];
            const folderIcon = window.icons.find(icon => icon.id === iconFolder);
            if (folderIcon) {
                lastIcon.kfolder = 0;
                lastIcon.region = folderIcon.region;
                lastIcon.position = folderIcon.position;
                
                // Delete the folder
                const folderIndex = window.icons.findIndex(icon => icon.id === iconFolder);
                if (folderIndex >= 0) {
                    window.icons.splice(folderIndex, 1);
                }
                
                await api.updateIcon(lastIcon);
                await api.deleteFolder(iconFolder);
                
                folder.closeFolderView();
                render.renderLaunchpad();
                return;
            }
        }
    }
    
    if (state.openFolder) {
        const folderStillHasIcons = window.icons.some(icon => icon.kfolder === state.openFolder.id);
        if (folderStillHasIcons) {
            folder.openFolderView(state.openFolder);
        } else {
            folder.closeFolderView();
            render.renderLaunchpad();
        }
    } else {
        render.renderLaunchpad();
    }
}

export function insertIconAtPosition(icon, targetRegion, targetPosition, insertBefore, targetFolder) {
    console.log('insertIconAtPosition called with:', {
        iconId: icon.id,
        iconTitle: icon.title,
        targetFolder: targetFolder,
        targetRegion,
        targetPosition,
        insertBefore
    });
    
    const layout = render.calculateLayout();
    const isFolder = targetFolder !== 0;
    
    // Determine current screen bounds
    const currentScreenNum = isFolder ? state.currentFolderScreen : state.currentScreen;
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = currentScreenNum * regionsPerScreen;
    const screenEndRegion = screenStartRegion + regionsPerScreen - 1;
    
    // Convert absolute region to screen-relative region for visual position calculation
    const screenRelativeRegion = targetRegion - screenStartRegion;
    
    // Calculate visual position of target icon using screen-relative region
    const targetVisualPos = render.calculateVisualPosition(screenRelativeRegion, targetPosition, layout);
    const rank = targetVisualPos.rank;
    
    // Where do we actually want to insert?
    const insertVisualFile = insertBefore ? targetVisualPos.file : targetVisualPos.file + 1;
    
    // Helper function to get current icon at a position (always fresh from icons array)
    const getIconAt = (region, position) => {
        return window.icons.find(ic => 
            ic.kfolder === targetFolder && 
            ic.id !== icon.id && 
            ic.region === region && 
            ic.position === position
        );
    };
    
    // Helper function to check if slot is dragged icon's original position
    const isOriginalPosition = (region, position) => {
        return state.dragState && 
            region === state.dragState.originalRegion && 
            position === state.dragState.originalPosition &&
            targetFolder === state.dragState.originalFolder;
    };
    
    // Build map of rank
    const totalFiles = layout.leaves * 4;
    const rankSlots = [];
    
    for (let file = 0; file < totalFiles; file++) {
        const slotInfo = render.visualPositionToRegionPosition(rank, file, layout);
        if (!slotInfo) continue;
        
        // Convert screen-relative region to absolute region
        const absoluteRegion = screenStartRegion + slotInfo.region;
        
        // Only include slots that are within the current screen's region range
        if (absoluteRegion < screenStartRegion || absoluteRegion > screenEndRegion) continue;
        
        rankSlots.push({
            file,
            region: absoluteRegion,
            position: slotInfo.position
        });
    }
    
    // Find nearest blank to left and right
    let nearestLeftBlank = null;
    let nearestRightBlank = null;
    
    for (const slot of rankSlots) {
        const hasIcon = getIconAt(slot.region, slot.position);
        const isOriginal = isOriginalPosition(slot.region, slot.position);
        
        if (!hasIcon || isOriginal) {
            if (slot.file < insertVisualFile) {
                if (!nearestLeftBlank || slot.file > nearestLeftBlank.file) {
                    nearestLeftBlank = slot;
                }
            } else if (slot.file > insertVisualFile) {
                if (!nearestRightBlank || slot.file < nearestRightBlank.file) {
                    nearestRightBlank = slot;
                }
            }
        }
    }

    console.log('rankSlots:', rankSlots);
    console.log('insertVisualFile:', insertVisualFile);
    console.log('nearestLeftBlank:', nearestLeftBlank);
    console.log('nearestRightBlank:', nearestRightBlank);

    
    // No space - fallback
    if (!nearestLeftBlank && !nearestRightBlank) {
        const fallbackPos = findFirstAvailablePosition(targetFolder);
        icon.region = fallbackPos.region;
        icon.position = fallbackPos.position;
        api.updateIcon(icon);
        return;
    }
    
    // Choose direction (prefer left on tie)
    const leftDist = nearestLeftBlank ? (insertVisualFile - nearestLeftBlank.file) - 1 : Infinity;
    const rightDist = nearestRightBlank ? (nearestRightBlank.file - insertVisualFile) : Infinity;
    const useLeft = leftDist <= rightDist;
    
    let insertSlot = rankSlots.find(s => s.file === insertVisualFile);
    
    // If insertVisualFile is beyond the rank (e.g., inserting after last icon)
    // use the last slot in the rank
    if (!insertSlot && insertVisualFile >= rankSlots[rankSlots.length - 1]?.file) {
        insertSlot = rankSlots[rankSlots.length - 1];
        console.log('insertVisualFile beyond rank, using last slot:', insertSlot);
    }

    if (!insertSlot) {
        const fallbackPos = findFirstAvailablePosition(targetFolder);
        icon.region = fallbackPos.region;
        icon.position = fallbackPos.position;
        api.updateIcon(icon);
        return;
    }


    console.log('Insert debug:', {
        targetRegion,
        targetPosition,
        insertBefore,
        targetVisualPos,
        insertVisualFile,
        nearestLeftBlank,
        nearestRightBlank,
        useLeft,
        insertSlot
    });
    
    // Log ALL icons in this context to see what we're working with
    console.log('All icons in targetFolder:', window.icons.filter(ic => ic.kfolder === targetFolder).map(ic => ({
        id: ic.id,
        title: ic.title,
        region: ic.region,
        position: ic.position
    })));
    
    // Also log what's currently at target and insert positions
    console.log('Icon at target position:', getIconAt(targetRegion, targetPosition));
    console.log('Icon at insert position:', getIconAt(insertSlot.region, insertSlot.position));

    // Collect all icons to update in a single batch
    const iconsToUpdate = [];

    if (useLeft) {
        // When insertBefore, stop before moving the target icon itself
        const shiftEndFile = insertBefore ? insertVisualFile - 1 : insertVisualFile;
        
        // If shiftEndFile is beyond the rank, use the last slot instead
        const actualShiftEndFile = Math.min(shiftEndFile, rankSlots[rankSlots.length - 1].file);
        
        // Shift left: Move icons from blank toward insert position
        for (let file = nearestLeftBlank.file; file < actualShiftEndFile; file++) {
            const destSlot = rankSlots.find(s => s.file === file);
            const sourceSlot = rankSlots.find(s => s.file === file + 1);
            
            if (!destSlot || !sourceSlot) continue;
            
            const iconAtSource = getIconAt(sourceSlot.region, sourceSlot.position);
            
            console.log(`Shift left file ${file}: moving from (${sourceSlot.region},${sourceSlot.position}) to (${destSlot.region},${destSlot.position})`, iconAtSource ? iconAtSource.title : 'NONE');
            
            if (iconAtSource) {
                iconAtSource.region = destSlot.region;
                iconAtSource.position = destSlot.position;
                iconsToUpdate.push(iconAtSource);
            }
        }
        
        // Place at the opened position
        const finalInsertSlot = rankSlots.find(s => s.file === actualShiftEndFile);
        console.log(`Placing ${icon.title} at final position (${finalInsertSlot.region},${finalInsertSlot.position})`);
        icon.kfolder = targetFolder; // Set the folder context
        icon.region = finalInsertSlot.region;
        icon.position = finalInsertSlot.position;
        iconsToUpdate.push(icon);
    } else {
        // Shift right: Start from blank and work BACKWARD toward insert position
        for (let file = nearestRightBlank.file; file > insertVisualFile; file--) {
            const destSlot = rankSlots.find(s => s.file === file);
            const sourceSlot = rankSlots.find(s => s.file === file - 1);
            
            if (!destSlot || !sourceSlot) continue;
            
            const iconAtSource = getIconAt(sourceSlot.region, sourceSlot.position);
            
            console.log(`Shift right file ${file}: moving from (${sourceSlot.region},${sourceSlot.position}) to (${destSlot.region},${destSlot.position})`, iconAtSource ? iconAtSource.title : 'NONE');
            
            if (iconAtSource) {
                iconAtSource.region = destSlot.region;
                iconAtSource.position = destSlot.position;
                iconsToUpdate.push(iconAtSource);
            }
        }
        
        // Place new icon at insert position
        console.log(`Placing ${icon.title} at insert position (${insertSlot.region},${insertSlot.position})`);
        
        icon.kfolder = targetFolder; // Set the folder context
        icon.region = insertSlot.region;
        icon.position = insertSlot.position;
        iconsToUpdate.push(icon);
    }

    // Single batch API call for all moved icons
    console.log(`Batch updating ${iconsToUpdate.length} icons:`, iconsToUpdate.map(ic => ic.title));
    api.updateIcons(iconsToUpdate);
}

export async function createFolder(icon1, icon2) {
    // Generate optimistic folder ID (negative number)
    const optimisticFolderId = -Math.abs(Date.now());
    
    // Create new folder with optimistic ID
    const newFolder = {
        id: optimisticFolderId,
        kfolder: 0,
        region: icon1.region,
        position: icon1.position,
        title: 'New Folder',
        type: 'folder'
    };
    
    window.icons.push(newFolder);
    
    // Move both icons into folder (using optimistic ID)
    icon1.kfolder = optimisticFolderId;
    icon1.region = 0;
    icon1.position = 0;
    
    icon2.kfolder = optimisticFolderId;
    icon2.region = 0;
    icon2.position = 1;
    
    // Call API to create folder and get real ID
    const result = await api.createFolder({
        optimistic_folder_id: optimisticFolderId,
        region: newFolder.region,
        position: newFolder.position,
        title: newFolder.title,
        icon1_id: icon1.id,
        icon2_id: icon2.id
    });
    
    if (result.success && result.folder_id) {
        // Replace optimistic ID with real ID throughout window.icons
        const folderIndex = window.icons.findIndex(ic => ic.id === optimisticFolderId);
        if (folderIndex >= 0) {
            window.icons[folderIndex].id = result.folder_id;
        }
        
        // Update the two icons to reference the real folder ID
        window.icons.forEach(ic => {
            if (ic.kfolder === optimisticFolderId) {
                ic.kfolder = result.folder_id;
            }
        });
        
        return result.folder_id;
    } else {
        // API call failed - keep optimistic ID for now
        // Service interruption flag is already set by api.createFolder
        console.warn('Folder creation API call failed, using optimistic ID');
        return optimisticFolderId;
    }
}
